import { useQuery } from '@tanstack/react-query'
import { fetchPersonaState } from '../lib/api'
import { useSSEConnected } from './useSSE'
import type { PersonaState, PersonaStatusEntry, PersonaStatusLevel } from '../lib/types'

function countFrom(val: unknown): number {
  if (typeof val === 'number') return val
  if (Array.isArray(val)) return val.length
  return 0
}

function deriveLevel(s: PersonaState): PersonaStatusLevel {
  if (!s.awake) return 'sleeping'
  if (s.zone === 'active') return 'active'
  if (s.zone === 'idle') return 'idle'
  const queue = (s.queue?.queued ?? s.queued_messages ?? 0) as number
  const tasks = countFrom(s.tasks ?? s.assigned_tasks ?? 0)
  const convs = countFrom(s.conversations ?? s.active_conversations ?? 0)
  if (queue > 0 || tasks > 0 || convs > 0) return 'active'
  return 'idle'
}

export function usePersonaStatusMap(): Record<string, PersonaStatusEntry> {
  const sseOk = useSSEConnected()
  const { data } = useQuery({
    queryKey: ['personaState'],
    queryFn: fetchPersonaState,
    refetchInterval: sseOk ? false : 30_000,
    staleTime: 10_000,
  })

  const out: Record<string, PersonaStatusEntry> = {}
  if (!data) return out

  for (const [name, state] of Object.entries(data)) {
    const s = state as PersonaState
    out[name.toLowerCase()] = {
      level: deriveLevel(s),
      awake: !!s.awake,
      queueCount: (s.queue?.queued ?? s.queued_messages ?? 0) as number,
      taskCount: countFrom(s.tasks ?? s.assigned_tasks ?? 0),
      contextPct: typeof s.context_pct === 'number' ? s.context_pct : null,
    }
  }
  return out
}
